import { use, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QueryViewerContext } from "@softmax/web-features/data";
import { currentTosVersion } from "@/lib/tos";
import {
  accountAvatarMutationOptions,
  accountProfileMutationOptions,
} from "../account.mutations";
import type { AccountUser } from "../account.types";
import type { AccountWrites } from "./use-account-writes";

type ProfileDraft = {
  name: string;
  consentServiceUpdates: boolean;
  consentMarketing: boolean;
};

type Preference = "consentServiceUpdates" | "consentMarketing";

export function useProfileEditor({
  savedUser,
  writes,
}: {
  savedUser: AccountUser;
  writes: AccountWrites;
}) {
  const queryClient = useQueryClient();
  const viewerId = use(QueryViewerContext);
  const save = useMutation(
    accountProfileMutationOptions({ queryClient, viewerId }),
  );
  const avatar = useMutation(
    accountAvatarMutationOptions({ queryClient, viewerId }),
  );
  const [draft, setDraft] = useState<Partial<ProfileDraft>>({});

  function setName(value: string | null) {
    setDraft((previous) => {
      const next = { ...previous };

      if (value === null || value === savedUser.name) delete next.name;
      else next.name = value;

      return next;
    });
    writes.note({ key: "name", notice: null });
  }

  async function saveField<K extends keyof ProfileDraft>({
    key,
    value,
  }: {
    key: K;
    value: ProfileDraft[K];
  }) {
    if (!writes.canWrite()) return false;

    setDraft((previous) => ({ ...previous, [key]: value }));
    return writes.run({
      key,
      progress: "Saving…",
      done: "Saved",
      work: async () => {
        await save.mutateAsync({
          name: savedUser.name ?? "",
          email: savedUser.email ?? "",
          institution: savedUser.institution,
          consentServiceUpdates: savedUser.consentServiceUpdates,
          consentMarketing: savedUser.consentMarketing,
          hasAcceptedTos: savedUser.tosVersion === currentTosVersion,
          [key]: value,
        });
        setDraft((previous) => {
          if (previous[key] !== value) return previous;

          const next = { ...previous };
          delete next[key];
          return next;
        });
        void writes.refreshSession();
      },
    });
  }

  async function saveName(value: string) {
    if (value.trim() === "") {
      writes.note({
        key: "name",
        notice: { tone: "error", text: "Your name is needed." },
      });
      return false;
    }

    return saveField({ key: "name", value: value.trim() });
  }

  function updatePhoto(file: File | null) {
    if (file && !writes.acceptable({ key: "photo", file })) return;

    void writes.run({
      key: "photo",
      progress: file ? "Uploading…" : "Removing…",
      done: file ? "Photo updated." : "Photo removed.",
      work: async () => {
        await avatar.mutateAsync(file);
        void writes.refreshSession();
      },
    });
  }

  return {
    saved: savedUser,
    draft,
    name: draft.name ?? savedUser.name ?? "",
    preferences: { ...savedUser, ...draft },
    unsaved: Object.keys(draft).length > 0,
    setName,
    saveName,
    updatePhoto,
    savePreference: ({ key, value }: { key: Preference; value: boolean }) => {
      void saveField({ key, value });
    },
  };
}
